import React, { useState } from 'react';
import { Mail, Phone, MapPin, Clock, Send } from 'lucide-react';
import { submitContact } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SEO from '@/components/SEO';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await submitContact(formData);
      toast.success('Thank you for contacting us! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Failed to submit the form. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Office Address',
      content: (
        <>
          N 102 Madhuvanti, Nanded City,<br />
          Sinhgad Road, Pune 411 041, India
        </>
      )
    },
    {
      icon: Phone,
      title: 'Phone',
      content: (
        <a href="tel:+912066872534" className="text-navy-600 hover:text-navy-700 font-medium">
          +91 20 6687 2534
        </a>
      )
    },
    {
      icon: Mail,
      title: 'Email',
      content: (
        <>
          <a href="mailto:contact@ixoraly.com" className="text-navy-600 hover:text-navy-700 font-medium block">
            contact@ixoraly.com
          </a>
          <a href="mailto:support@ixoraly.com" className="text-navy-600 hover:text-navy-700 font-medium block mt-1">
            support@ixoraly.com
          </a>
        </>
      )
    },
    {
      icon: Clock,
      title: 'Business Hours',
      content: (
        <>
          Monday - Friday<br />
          9:00 AM - 6:00 PM IST
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Contact Us"
        description="Get in touch with Ixoraly for press release distribution inquiries, pricing questions, or partnership opportunities. We respond within 24 hours."
        path="/contact"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold mb-6" data-testid="contact-heading">
              Get In Touch
            </h1>
            <p className="text-xl text-gray-300">
              At Ixoraly, we're here to help you share your story with the world. Whether you have a question about our services, need support, or want a custom plan — our team is just a message away.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                    Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    data-testid="contact-name-input"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full py-3"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="contact-email-input"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your.email@example.com"
                    className="w-full py-3"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                    Message *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    data-testid="contact-message-input"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    placeholder="Tell us how we can help..."
                    className="w-full"
                  />
                </div>

                <Button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={loading}
                  className="w-full bg-navy-600 hover:bg-navy-700 text-white py-4 text-base font-semibold"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      Send Message
                      <Send size={18} className="ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  {contactInfo.map((info, index) => {
                    const Icon = info.icon;
                    return (
                      <div key={index} className="bg-navy-50 rounded-xl p-6">
                        <div className="w-12 h-12 bg-navy-100 rounded-lg flex items-center justify-center mb-4">
                          <Icon className="text-navy-600" size={24} />
                        </div>
                        <h3 className="font-bold text-gray-900 mb-2">{info.title}</h3>
                        <div className="text-gray-600 text-sm">{info.content}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Response Time */}
              <div className="bg-gradient-to-br from-navy-600 to-navy-700 rounded-xl p-8 text-white">
                <h3 className="font-bold text-xl mb-3">Quick Response Guarantee</h3>
                <p className="text-navy-100 mb-4">
                  We value your time. Our team typically responds within 24-48 business hours.
                </p>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-sm">Support team online</span>
                </div>
              </div>

              {/* Map placeholder */}
              <div className="bg-gray-100 rounded-xl p-8 text-center">
                <div className="w-16 h-16 bg-navy-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="text-navy-600" size={28} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Visit Our Office</h3>
                <p className="text-gray-600 text-sm">
                  N 102 Madhuvanti, Nanded City,<br />
                  Sinhgad Road, Pune 411 041, India
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
